import React, { useEffect, useMemo, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import SearchIcon from "@mui/icons-material/Search";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import {
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import AgGridTable from "../../components/table/AgGridTable";
import WhiteTooltip from "../../components/WhiteTooltip";
import type { DotControl, DotHistoryEntry } from "./types";

type HistoryTab = "all" | "control" | "override" | "status" | "other";
type TimeRange = "any" | "24h" | "7d" | "30d";

const ACCENT = "#4F46E5";

function normalizeField(field: string): string {
  return String(field || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

function getChangeKind(
  field: string
): "status" | "override" | "other" {
  const f = normalizeField(field);
  if (f === "status") return "status";
  if (f.includes("override")) return "override";
  return "other";
}

function formatFieldLabel(field: string): string {
  const raw = String(field || "").trim();
  if (!raw) return "—";
  return raw.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatHistoryValue(v: unknown): string {
  if (v == null || String(v).trim() === "") return "—";
  if (v === true || v === "true" || v === "True") return "Yes";
  if (v === false || v === "false" || v === "False") return "No";
  return String(v);
}

function formatUpdatedOnInline(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const day = String(d.getDate()).padStart(2, "0");
  const mon = d.toLocaleString("en-GB", { month: "short" });
  const year = d.getFullYear();
  const time = d.toLocaleString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${day} ${mon} ${year}, ${time}`;
}

function userInitials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase() || "—";
}

function MiniSpark({
  color,
  points,
}: {
  color: string;
  points: string;
}) {
  return (
    <Box
      component="svg"
      viewBox="0 0 64 24"
      sx={{ width: 56, height: 22, flexShrink: 0, opacity: 0.9 }}
    >
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={points}
      />
    </Box>
  );
}

interface Props {
  open: boolean;
  onClose: () => void;
  history: DotHistoryEntry[];
  controls: DotControl[];
  onOpenControl?: (control: DotControl) => void;
}

const DoTHistoryDrawer: React.FC<Props> = ({
  open,
  onClose,
  history,
  controls,
  onOpenControl,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const muted = isDark ? "rgba(255,255,255,0.55)" : "#6B7280";
  const border = isDark ? "rgba(255,255,255,0.1)" : "#E8ECF4";

  const [tab, setTab] = useState<HistoryTab>("all");
  const [search, setSearch] = useState("");
  const [timeRange, setTimeRange] = useState<TimeRange>("any");
  const [timeMenuEl, setTimeMenuEl] = useState<null | HTMLElement>(null);
  const [rowMenu, setRowMenu] = useState<{
    el: HTMLElement;
    row: DotHistoryEntry;
  } | null>(null);

  useEffect(() => {
    if (!open) return;
    setTab("all");
    setSearch("");
    setTimeRange("any");
    setTimeMenuEl(null);
    setRowMenu(null);
  }, [open]);

  const stats = useMemo(() => {
    let overrides = 0;
    let status = 0;
    let other = 0;
    const controlIds = new Set<string>();
    for (const h of history) {
      controlIds.add(h.control_id);
      const kind = getChangeKind(h.field);
      if (kind === "override") overrides += 1;
      else if (kind === "status") status += 1;
      else other += 1;
    }
    return {
      total: history.length,
      controls: controlIds.size,
      overrides,
      status,
      other,
    };
  }, [history]);

  const filteredHistory = useMemo(() => {
    const q = search.trim().toLowerCase();
    const now = Date.now();
    const cutoff =
      timeRange === "24h"
        ? now - 24 * 60 * 60 * 1000
        : timeRange === "7d"
          ? now - 7 * 24 * 60 * 60 * 1000
          : timeRange === "30d"
            ? now - 30 * 24 * 60 * 60 * 1000
            : null;

    let rows = history.filter((h) => {
      if (cutoff != null) {
        const t = h.updated_at ? new Date(h.updated_at).getTime() : 0;
        if (!t || t < cutoff) return false;
      }
      if (tab === "override" && getChangeKind(h.field) !== "override")
        return false;
      if (tab === "status" && getChangeKind(h.field) !== "status") return false;
      if (tab === "other" && getChangeKind(h.field) !== "other") return false;
      if (q) {
        const hay = [
          h.control_id,
          h.control_title,
          h.field,
          h.old_value,
          h.new_value,
          h.updated_by,
        ]
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    if (tab === "control") {
      const latestByControl = new Map<string, DotHistoryEntry>();
      for (const h of rows) {
        const prev = latestByControl.get(h.control_id);
        const t = h.updated_at ? new Date(h.updated_at).getTime() : 0;
        const pt = prev?.updated_at ? new Date(prev.updated_at).getTime() : 0;
        if (!prev || t >= pt) latestByControl.set(h.control_id, h);
      }
      rows = Array.from(latestByControl.values());
    }

    return [...rows].sort((a, b) => {
      const ta = a.updated_at ? new Date(a.updated_at).getTime() : 0;
      const tb = b.updated_at ? new Date(b.updated_at).getTime() : 0;
      return tb - ta;
    });
  }, [history, search, tab, timeRange]);

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Control",
        field: "control_id",
        minWidth: 100,
        flex: 0.85,
        filter: true,
        cellStyle: { display: "flex", alignItems: "center" },
        cellRenderer: (p: ICellRendererParams) => {
          const id = p.data?.control_id || "—";
          const title = (p.data?.control_title || "").trim();
          return (
            <WhiteTooltip title={title ? `${id} — ${title}` : id}>
              <Typography
                onClick={(e) => {
                  e.stopPropagation();
                  const c = controls.find((x) => x.id === id);
                  if (c && onOpenControl) onOpenControl(c);
                }}
                sx={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: ACCENT,
                  cursor: onOpenControl ? "pointer" : "default",
                  "&:hover": onOpenControl
                    ? { textDecoration: "underline" }
                    : undefined,
                }}
              >
                {id}
              </Typography>
            </WhiteTooltip>
          );
        },
      },
      {
        headerName: "Field",
        field: "field",
        minWidth: 140,
        flex: 1.05,
        filter: true,
        cellStyle: { display: "flex", alignItems: "center" },
        cellRenderer: (p: ICellRendererParams) => {
          const raw = String(p.value || "").trim();
          const key = normalizeField(raw);
          const label = formatFieldLabel(raw);
          let Icon = EditOutlinedIcon;
          let color = isDark ? "#FDBA74" : "#EA580C";
          if (key === "status") {
            Icon = MonitorHeartOutlinedIcon;
            color = isDark ? "#93C5FD" : "#2563EB";
          } else if (key === "override") {
            Icon = LayersOutlinedIcon;
            color = isDark ? "#86EFAC" : "#16A34A";
          } else if (key.includes("override_reason") || key.includes("reason")) {
            Icon = ChatBubbleOutlineRoundedIcon;
            color = isDark ? "#C4B5FD" : "#7C3AED";
          }
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Icon sx={{ fontSize: 16, color }} />
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                {label}
              </Typography>
            </Box>
          );
        },
      },
      {
        headerName: "From",
        field: "old_value",
        flex: 0.9,
        minWidth: 90,
        tooltipValueGetter: (p) => formatHistoryValue(p.value),
        valueFormatter: (p) => formatHistoryValue(p.value),
      },
      {
        headerName: "To",
        field: "new_value",
        flex: 1.15,
        minWidth: 110,
        tooltipValueGetter: (p) => formatHistoryValue(p.value),
        valueFormatter: (p) => formatHistoryValue(p.value),
      },
      {
        headerName: "Change type",
        field: "field",
        colId: "change_type",
        minWidth: 140,
        flex: 1.1,
        cellStyle: { display: "flex", alignItems: "center" },
        valueGetter: (p) => getChangeKind(p.data?.field || ""),
        cellRenderer: (p: ICellRendererParams) => {
          const kind = getChangeKind(p.data?.field || "");
          const cfg =
            kind === "status"
              ? {
                  label: "Status change",
                  Icon: MonitorHeartOutlinedIcon,
                  bg: isDark ? "rgba(37,99,235,0.2)" : "#EFF6FF",
                  fg: isDark ? "#93C5FD" : "#2563EB",
                }
              : kind === "override"
                ? {
                    label: "Override",
                    Icon: LayersOutlinedIcon,
                    bg: isDark ? "rgba(22,163,74,0.2)" : "#ECFDF5",
                    fg: isDark ? "#86EFAC" : "#16A34A",
                  }
                : {
                    label: "Other change",
                    Icon: EditOutlinedIcon,
                    bg: isDark ? "rgba(124,58,237,0.2)" : "#F5F3FF",
                    fg: isDark ? "#C4B5FD" : "#7C3AED",
                  };
          return (
            <Chip
              size="small"
              icon={<cfg.Icon sx={{ fontSize: "14px !important" }} />}
              label={cfg.label}
              sx={{
                height: 26,
                fontWeight: 700,
                fontSize: 12,
                bgcolor: cfg.bg,
                color: cfg.fg,
                border: "none",
                "& .MuiChip-icon": { color: cfg.fg },
              }}
            />
          );
        },
      },
      {
        headerName: "Updated by",
        field: "updated_by",
        minWidth: 150,
        flex: 1.15,
        filter: true,
        cellStyle: { display: "flex", alignItems: "center" },
        cellRenderer: (p: ICellRendererParams) => {
          const name = String(p.value || "").trim() || "—";
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  bgcolor: isDark ? "rgba(79,70,229,0.35)" : ACCENT,
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {userInitials(name)}
              </Box>
              <Typography
                title={name}
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {name}
              </Typography>
            </Box>
          );
        },
      },
      {
        headerName: "Updated on",
        field: "updated_at",
        minWidth: 200,
        flex: 1.35,
        sortable: true,
        filter: false,
        cellStyle: { display: "flex", alignItems: "center" },
        comparator: (a: string | null, b: string | null) => {
          const ta = a ? new Date(a).getTime() : 0;
          const tb = b ? new Date(b).getTime() : 0;
          return (Number.isNaN(ta) ? 0 : ta) - (Number.isNaN(tb) ? 0 : tb);
        },
        cellRenderer: (p: ICellRendererParams) => {
          const label = formatUpdatedOnInline(p.data?.updated_at);
          if (label === "—") {
            return (
              <Box
                sx={{
                  px: 1,
                  py: 0.25,
                  borderRadius: 1.25,
                  bgcolor: isDark ? "rgba(255,255,255,0.06)" : "#F3F4F6",
                  color: muted,
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                –
              </Box>
            );
          }
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <CalendarMonthOutlinedIcon
                sx={{ fontSize: 16, color: isDark ? "#93C5FD" : "#2563EB" }}
              />
              <Typography
                sx={{
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: isDark ? "#E5E7EB" : "#1E293B",
                  whiteSpace: "nowrap",
                }}
              >
                {label}
              </Typography>
            </Box>
          );
        },
      },
      {
        headerName: "",
        colId: "actions",
        width: 52,
        maxWidth: 52,
        flex: 0,
        sortable: false,
        filter: false,
        resizable: false,
        suppressSizeToFit: true,
        cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
        cellRenderer: (p: ICellRendererParams) => (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              if (!p.data) return;
              setRowMenu({ el: e.currentTarget, row: p.data });
            }}
            sx={{ color: muted }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        ),
      },
    ],
    [controls, isDark, muted, onOpenControl]
  );

  const tabs: { key: HistoryTab; label: string; count: number }[] = [
    { key: "all", label: "All", count: stats.total },
    { key: "control", label: "Control", count: stats.controls },
    { key: "override", label: "Override", count: stats.overrides },
    { key: "status", label: "Status", count: stats.status },
    { key: "other", label: "Other", count: stats.other },
  ];

  const timeLabel =
    timeRange === "24h"
      ? "Last 24 hours"
      : timeRange === "7d"
        ? "Last 7 days"
        : timeRange === "30d"
          ? "Last 30 days"
          : "Any time";

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: { xs: "100%", md: "72%", lg: "68%" },
          maxWidth: 1180,
          p: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          bgcolor: isDark ? "#191C24" : "#F4F6FB",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1.5,
          px: 2.5,
          pt: 2.25,
          pb: 2,
          bgcolor: isDark ? "#191C24" : "#FFFFFF",
          borderBottom: `1px solid ${border}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "50%",
              bgcolor: isDark ? "rgba(79,70,229,0.22)" : "#EEF2FF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <HistoryIcon sx={{ fontSize: 24, color: ACCENT }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 20, fontWeight: 800, lineHeight: 1.2 }}>
              Recent updates
            </Typography>
            <Typography sx={{ mt: 0.4, fontSize: 13, color: muted }}>
              {stats.total} change{stats.total === 1 ? "" : "s"} recorded for
              DoT / MBSS controls
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: muted }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ px: 2.5, pt: 2, pb: 1.25 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
            gap: 1.25,
          }}
        >
          {(
            [
              {
                label: "Total updates",
                value: stats.total,
                icon: HistoryIcon,
                iconBg: isDark ? "rgba(79,70,229,0.22)" : "#EEF2FF",
                iconColor: ACCENT,
                spark: "#818CF8",
                points: "2,18 14,14 26,16 38,8 50,11 62,4",
              },
              {
                label: "Overrides",
                value: stats.overrides,
                icon: CheckCircleOutlineRoundedIcon,
                iconBg: isDark ? "rgba(22,163,74,0.2)" : "#ECFDF5",
                iconColor: "#16A34A",
                spark: "#34D399",
                points: "2,16 14,12 26,14 38,9 50,11 62,6",
              },
              {
                label: "Status changes",
                value: stats.status,
                icon: InfoOutlinedIcon,
                iconBg: isDark ? "rgba(37,99,235,0.2)" : "#EFF6FF",
                iconColor: "#2563EB",
                spark: "#60A5FA",
                points: "2,14 14,17 26,12 38,15 50,8 62,10",
              },
              {
                label: "Other changes",
                value: stats.other,
                icon: EditOutlinedIcon,
                iconBg: isDark ? "rgba(234,88,12,0.2)" : "#FFF7ED",
                iconColor: "#EA580C",
                spark: "#FB923C",
                points: "2,12 14,15 26,10 38,13 50,7 62,9",
              },
            ] as const
          ).map((card) => (
            <Box
              key={card.label}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.1,
                px: 1.5,
                py: 1.2,
                borderRadius: 2.5,
                bgcolor: isDark ? "rgba(255,255,255,0.04)" : "#FFFFFF",
                border: `1px solid ${border}`,
                boxShadow: isDark
                  ? "none"
                  : "0 1px 2px rgba(15, 23, 42, 0.04)",
              }}
            >
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  bgcolor: card.iconBg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <card.icon sx={{ fontSize: 17, color: card.iconColor }} />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{ fontSize: 11, fontWeight: 600, color: muted }}
                >
                  {card.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 22,
                    fontWeight: 800,
                    lineHeight: 1.1,
                    color: isDark ? "#E5E7EB" : "#111827",
                  }}
                >
                  {card.value}
                </Typography>
              </Box>
              <MiniSpark color={card.spark} points={card.points} />
            </Box>
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          px: 2.5,
          pb: 1.25,
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        {tabs.map((t) => {
          const active = tab === t.key;
          return (
            <Chip
              key={t.key}
              clickable
              onClick={() => setTab(t.key)}
              label={`${t.label} (${t.count})`}
              sx={{
                height: 30,
                fontWeight: 700,
                fontSize: 12.5,
                bgcolor: active
                  ? isDark
                    ? "rgba(79,70,229,0.28)"
                    : "#EEF2FF"
                  : isDark
                    ? "rgba(255,255,255,0.04)"
                    : "#FFFFFF",
                color: active
                  ? isDark
                    ? "#C7D2FE"
                    : "#4338CA"
                  : isDark
                    ? "rgba(255,255,255,0.75)"
                    : "#475569",
                border: `1px solid ${
                  active
                    ? isDark
                      ? "rgba(129,140,248,0.45)"
                      : "#C7D2FE"
                    : border
                }`,
              }}
            />
          );
        })}
      </Box>

      <Box
        sx={{
          px: 2.5,
          pb: 1.5,
          display: "flex",
          gap: 1,
          alignItems: "center",
        }}
      >
        <TextField
          size="small"
          fullWidth
          placeholder="Search updates by control, field, user…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: muted }} />
              </InputAdornment>
            ),
          }}
          sx={{
            bgcolor: isDark ? "rgba(255,255,255,0.04)" : "#FFFFFF",
            borderRadius: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              fontSize: 13,
            },
          }}
        />
        <Button
          variant="outlined"
          size="small"
          onClick={(e) => setTimeMenuEl(e.currentTarget)}
          startIcon={<CalendarMonthOutlinedIcon sx={{ fontSize: 16 }} />}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: 2,
            whiteSpace: "nowrap",
            bgcolor: isDark ? "rgba(255,255,255,0.04)" : "#FFFFFF",
            borderColor: border,
            color: isDark ? "#E5E7EB" : "#334155",
            minWidth: 130,
          }}
        >
          {timeLabel}
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<FilterListRoundedIcon sx={{ fontSize: 16 }} />}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: 2,
            whiteSpace: "nowrap",
            bgcolor: isDark ? "rgba(255,255,255,0.04)" : "#FFFFFF",
            borderColor: border,
            color: isDark ? "#E5E7EB" : "#334155",
          }}
        >
          Filters
        </Button>
      </Box>

      <Menu
        anchorEl={timeMenuEl}
        open={Boolean(timeMenuEl)}
        onClose={() => setTimeMenuEl(null)}
      >
        {(
          [
            ["any", "Any time"],
            ["24h", "Last 24 hours"],
            ["7d", "Last 7 days"],
            ["30d", "Last 30 days"],
          ] as const
        ).map(([key, label]) => (
          <MenuItem
            key={key}
            selected={timeRange === key}
            onClick={() => {
              setTimeRange(key);
              setTimeMenuEl(null);
            }}
          >
            {label}
          </MenuItem>
        ))}
      </Menu>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          px: 2.5,
          pb: 2,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            borderRadius: 2.5,
            bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#FFFFFF",
            border: `1px solid ${border}`,
            overflow: "hidden",
            px: 1.25,
            pt: 0.75,
            pb: 0.5,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {filteredHistory.length === 0 ? (
            <Box
              sx={{
                height: "100%",
                minHeight: 240,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: 1,
                color: muted,
              }}
            >
              <TuneRoundedIcon sx={{ fontSize: 34, opacity: 0.45 }} />
              <Typography sx={{ fontSize: 14 }}>
                No updates match the current filters.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ flex: 1, minHeight: 0, height: "100%" }}>
              <AgGridTable
                columnDefs={columnDefs}
                data={filteredHistory}
                height="100%"
                showSearch={false}
                pagination
                paginationPageSize={20}
                showAppsIcon={false}
                styles={{
                  height: "100%",
                  "& .ag-header-cell-label": { overflow: "visible" },
                  "& .ag-header-cell-text": {
                    overflow: "visible !important",
                    textOverflow: "clip !important",
                    whiteSpace: "nowrap",
                  },
                }}
              />
            </Box>
          )}
        </Box>
      </Box>

      <Menu
        anchorEl={rowMenu?.el || null}
        open={Boolean(rowMenu)}
        onClose={() => setRowMenu(null)}
      >
        <MenuItem
          onClick={() => {
            const id = rowMenu?.row.control_id;
            const c = controls.find((x) => x.id === id);
            setRowMenu(null);
            if (c && onOpenControl) onOpenControl(c);
          }}
        >
          Open control
        </MenuItem>
      </Menu>
    </Drawer>
  );
};

export default DoTHistoryDrawer;
